import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const getFloatingDayParts = (value, userTimezone) => {
  if (!value) {
    const today = userTimezone ? dayjs().tz(userTimezone) : dayjs();
    return {
      year: today.year(),
      month: today.month(),
      day: today.date(),
    };
  }

  if (dayjs.isDayjs(value)) {
    return {
      year: value.year(),
      month: value.month(),
      day: value.date(),
    };
  }

  const dateValue = value instanceof Date ? value : new Date(value);
  return {
    year: dateValue.getFullYear(),
    month: dateValue.getMonth(),
    day: dateValue.getDate(),
  };
};

const useSchedule = (selectedTab = 'all', userTimezone) => {
  const scheduleApi = useSelector((state) => state.apis.scheduleApi);
  const [scheduleList, setScheduleList] = useState([]);
  const [fetchingScheduleList, setFetchingScheduleList] = useState(false);
  const cacheRef = useRef(new Map());

  const fetchSchedule = useCallback(
    (useruuid, endDate, plusDays = 0, forceRefresh = false, nextDays = 0) => {
      const errorCallback = (error) => {
        setFetchingScheduleList(false);
        console.error('Error fetching Events:', error);
      };

      const EventType = selectedTab === 'call' ? 'all' : selectedTab;
      const opts = { timezone: userTimezone, status: true };
      try {
        const { year, month, day } = getFloatingDayParts(endDate, userTimezone);
        const baseDayKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const baseDay = userTimezone
          ? dayjs.tz(baseDayKey, 'YYYY-MM-DD', userTimezone)
          : dayjs(new Date(year, month, day));
        const endDay = baseDay.add(nextDays, 'day');
        const startDay = baseDay.subtract(plusDays, 'day');
        const endDateTime = `${endDay.format('YYYY-MM-DD')}T23:59:00.000Z`;
        const startDateTime = `${startDay.format('YYYY-MM-DD')}T00:00:00.000Z`;

        const cacheKey = `${useruuid}|${startDateTime}|${endDateTime}|${EventType}`;

        if (!forceRefresh && cacheRef.current.has(cacheKey)) {
          setScheduleList(cacheRef.current.get(cacheKey));
          return;
        }

        setFetchingScheduleList(true);

        const successCallback = (data) => {
          setFetchingScheduleList(false);
          const events = data.schedule_events;
          cacheRef.current.set(cacheKey, events);
          setScheduleList(events);
        };

        scheduleApi.eventsByEventTypesTimeRange(
          useruuid,
          startDateTime,
          endDateTime,
          EventType,
          opts,
          apiCallback({
            failCallback: errorCallback,
            errorCallback,
            successCallback,
          })
        );
      } catch (error) {
        errorCallback(error);
      }
    },
    [scheduleApi, selectedTab, userTimezone]
  );

  return [fetchingScheduleList, scheduleList, fetchSchedule];
};

export default useSchedule;
